import type { ReactElement } from 'react';
import type { CalendarEntry, MealSlot, MonthCell } from '../../api/calendarTypes.js';
import { MEAL_ICON, MEAL_SLOTS } from '../../api/calendarTypes.js';

export interface DayCellProps {
  cell: MonthCell;
  onSelect: (iso: string) => void;
}

export function DayCell({ cell, onSelect }: DayCellProps): ReactElement {
  const disabled = !cell.inMonth;
  const entries = cell.entries;
  const slots = groupBySlot(entries);
  const mealLines = MEAL_SLOTS.map((slot) => {
    const list = slots.get(slot) ?? [];
    if (list.length === 0) return null;
    const first = list[0]!;
    const extra = list.length - 1;
    return (
      <div
        key={slot}
        className="flex min-w-0 items-center gap-1 text-[11px] leading-tight"
        style={{ color: 'var(--text-main)' }}
        data-testid={`day-cell-line-${cell.iso}-${slot}`}
      >
        <span aria-hidden="true" className="shrink-0">
          {MEAL_ICON[slot]}
        </span>
        <span className="truncate">{first.recipe.title}</span>
        {extra > 0 ? (
          <span
            className="shrink-0 text-[10px]"
            style={{ color: 'var(--text-muted)' }}
          >
            +{extra}
          </span>
        ) : null}
      </div>
    );
  });

  const hasLeftover = entries.some((e) => e.is_leftover);

  return (
    <button
      type="button"
      onClick={() => {
        if (disabled) return;
        onSelect(cell.iso);
      }}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={cell.iso}
      className="flex h-full min-h-[88px] flex-col gap-1 rounded-lg border p-2 text-left"
      style={{
        borderColor: cell.isSelected
          ? 'var(--accent)'
          : 'var(--border)',
        background: cell.isSelected
          ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
          : cell.inMonth
            ? 'var(--bg-surface)'
            : 'transparent',
        color: cell.inMonth ? 'var(--text-main)' : 'var(--text-muted)',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
      data-testid={`day-cell-${cell.iso}`}
      data-in-month={cell.inMonth ? 'true' : 'false'}
      data-today={cell.isToday ? 'true' : 'false'}
      data-selected={cell.isSelected ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between">
        <span
          className="text-sm font-semibold"
          style={{
            color: cell.isToday ? 'var(--accent)' : 'var(--text-main)',
          }}
        >
          {cell.dayOfMonth}
        </span>
        {cell.isToday ? (
          <span
            className="rounded-full px-1.5 text-[9px] uppercase"
            style={{
              background: 'color-mix(in srgb, var(--accent) 15%, transparent)',
              color: 'var(--accent)',
            }}
            data-testid={`day-cell-badge-${cell.iso}`}
          >
            today
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5">
        {mealLines}
        {hasLeftover ? (
          <span
            className="text-[10px] uppercase"
            style={{ color: 'var(--text-muted)' }}
          >
            leftover
          </span>
        ) : null}
      </div>
    </button>
  );
}

function groupBySlot(entries: CalendarEntry[]): Map<MealSlot, CalendarEntry[]> {
  const map = new Map<MealSlot, CalendarEntry[]>();
  for (const e of entries) {
    const arr = map.get(e.meal_type) ?? [];
    arr.push(e);
    map.set(e.meal_type, arr);
  }
  return map;
}
